import { useQuery } from "@tanstack/react-query";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { InvestorSidebar } from "@/components/dashboard/sidebar";
import { MetricsCard } from "@/components/dashboard/metrics-card";
import { PerformanceChart } from "@/components/dashboard/performance-chart";
import { AllocationChart } from "@/components/dashboard/allocation-chart";
import { AnnouncementList } from "@/components/dashboard/announcement-list";
import { ThemeToggle } from "@/components/theme-toggle";
import { TrendingUp, Wallet, BarChart3, Target, Bell } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import type { Portfolio, Announcement, InvestorProfile, Transaction, Notification } from "@shared/schema";
import { formatPlatformCurrency } from "@/lib/currency";

const formatCurrency = formatPlatformCurrency;

const MONTH_ORDER: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

type InvestorReturnRecord = {
  month: string;
  year: number;
  returnsAmount: string | number;
  returnsPercentage?: string | number;
  isWithdrawn?: boolean;
};

const generatePerformanceData = (
  portfolio: Portfolio | null | undefined,
  returns: InvestorReturnRecord[] = []
) => {
  if (!portfolio) return [];

  const baseValue = Number(portfolio.totalInvested || 0);
  if (returns.length === 0) {
    const currentValue = Number(portfolio.currentValue || baseValue);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months.map((month, index) => ({
      date: month,
      value: Math.round(baseValue + ((currentValue - baseValue) * (index + 1)) / 12),
    }));
  }

  const sortedReturns = [...returns].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return (MONTH_ORDER[a.month] ?? 0) - (MONTH_ORDER[b.month] ?? 0);
  });

  let cumulativeValue = baseValue;
  const points: Array<{ date: string; value: number }> = [
    { date: "Invested", value: Math.round(baseValue) },
  ];

  sortedReturns.forEach((record) => {
    if (!record.isWithdrawn) {
      cumulativeValue += Number(record.returnsAmount || 0);
    }
    points.push({
      date: `${record.month} ${record.year}`,
      value: Math.round(cumulativeValue),
    });
  });

  return points;
};

function buildPortfolioAllocationData(portfolio: Portfolio | null | undefined) {
  const privateCredit = parseFloat(portfolio?.privateCreditAllocation || "0");
  const aifExposure = parseFloat(portfolio?.aifExposure || "0");
  const cash = parseFloat(portfolio?.cashEquivalents || "0");
  const currencyCommodities = parseFloat((portfolio as any)?.currencyCommodities || "0");

  return [
    { name: "Private Credit", value: privateCredit, color: "hsl(var(--accent))" },
    { name: "AIF Exposure", value: aifExposure, color: "hsl(var(--chart-2))" },
    { name: "Cash & Equivalents", value: cash, color: "hsl(var(--chart-3))" },
    { name: "Currency/Commodities", value: currencyCommodities, color: "hsl(280, 60%, 55%)" },
  ].filter((item) => item.value > 0);
}

interface DashboardData {
  profile: InvestorProfile;
  portfolio: Portfolio;
  recentTransactions: Transaction[];
  announcements: Announcement[];
}

export default function InvestorDashboard() {
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery<DashboardData>({
    queryKey: ["/api/investor/dashboard"],
  });

  const { data: returnsData } = useQuery<InvestorReturnRecord[]>({
    queryKey: ["/api/investor/returns"],
  });

  const { data: notifications } = useQuery<Notification[]>({
    queryKey: ["/api/investor/notifications"],
  });

  const portfolio = dashboardData?.portfolio;
  const announcements = dashboardData?.announcements || [];
  const unreadNotifications = notifications?.filter(n => !n.isRead).length || 0;
  const allocationData = buildPortfolioAllocationData(portfolio);

  const sidebarStyle = {
    "--sidebar-width": "17rem",
    "--sidebar-width-icon": "3.5rem",
  };

  return (
    <SidebarProvider style={sidebarStyle as React.CSSProperties}>
      <div className="flex h-screen w-full overflow-hidden">
        <InvestorSidebar />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <header className="flex items-center justify-between gap-4 h-16 px-6 border-b bg-background/95 backdrop-blur shrink-0">
            <div className="flex items-center gap-4">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <div>
                <h1 className="text-lg font-semibold">Dashboard</h1>
                <p className="text-sm text-muted-foreground">
                  Welcome{dashboardData?.profile?.firstName ? `, ${dashboardData.profile.firstName}` : ""}
                  {dashboardData?.profile?.clientId ? (
                    <span className="ml-2 font-mono text-xs text-accent">· {dashboardData.profile.clientId}</span>
                  ) : null}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/dashboard/announcements">
                <Button variant="ghost" size="icon" className="relative" data-testid="button-notifications">
                  <Bell className="h-5 w-5" />
                  {unreadNotifications > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                      {unreadNotifications}
                    </Badge>
                  )}
                </Button>
              </Link>
              <ThemeToggle />
            </div>
          </header>

          <main className="flex-1 p-6 space-y-6 overflow-y-auto">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricsCard
                title="Total Invested"
                value={formatCurrency(portfolio?.totalInvested)}
                icon={<Wallet className="w-5 h-5 text-accent" />}
                delay={0}
                isLoading={dashboardLoading}
              />
              <MetricsCard
                title="Current Value"
                value={formatCurrency(portfolio?.currentValue)}
                change={portfolio && Number(portfolio.totalInvested) > 0 ? `+${((Number(portfolio.currentValue) / Number(portfolio.totalInvested) - 1) * 100).toFixed(1)}%` : undefined}
                changeType="positive"
                description="Since inception"
                icon={<TrendingUp className="w-5 h-5 text-accent" />}
                delay={0.08}
                isLoading={dashboardLoading}
              />
              <MetricsCard
                title="Net Returns"
                value={formatCurrency(portfolio?.returns)}
                changeType="positive"
                description="Profit Earned"
                icon={<BarChart3 className="w-5 h-5 text-accent" />}
                delay={0.16}
                isLoading={dashboardLoading}
              />
              <MetricsCard
                title="Net IRR"
                value={portfolio ? `${Number(portfolio.irr).toFixed(2)}%` : "—"}
                changeType="positive"
                description="Annualized"
                icon={<Target className="w-5 h-5 text-accent" />}
                delay={0.24}
                isLoading={dashboardLoading}
              />
            </div>

            <motion.div
              className="grid lg:grid-cols-3 gap-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 }}
            >
              <div className="lg:col-span-2">
                <PerformanceChart
                  data={generatePerformanceData(portfolio, returnsData || [])}
                  title="Portfolio Performance"
                />
              </div>
              <div>
                <AllocationChart data={allocationData} />
              </div>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-6">
              <AnnouncementList
                announcements={announcements}
                isLoading={dashboardLoading}
              />
              <div className="p-6 rounded-xl bg-gradient-to-br from-accent/10 to-primary/10 border">
                <h3 className="font-semibold mb-2">Need Assistance?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Our dedicated relationship manager is here to help with any questions about your investment.
                </p>
                <div className="text-sm">
                  <p className="font-medium">Contact Support</p>
                  <p className="text-muted-foreground">investor.relations@godmancapital.in</p>
                  <p className="text-muted-foreground">+91 98765 43210</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
